import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import DisplayPanel from './components/DisplayPanel';
import './App.css';

// Importar sonido
import notificationSound from './assets/sounds/notification.mp3';

const API_BASE_URL = process.env.NODE_ENV !== 'development' 
  ? 'https://tickets.fortunachile.cl:5000'
  : 'http://localhost:5000';

const socketConfig = {
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
  forceNew: true,
  withCredentials: true
};

function App() {
  const [currentTicket, setCurrentTicket] = useState(0);
  const [previousTicket, setPreviousTicket] = useState(0);
  const [ticketLimits, setTicketLimits] = useState({ min: 0, max: 99 });
  const [connectionStatus, setConnectionStatus] = useState('Conectando...');
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [volume, setVolume] = useState(0.7);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const audioRef = useRef(null);
  const previousTicketRef = useRef(0);

  // Detectar cambio de tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cargar sonido
  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.volume = volume;
    audioRef.current.preload = 'auto';
  }, [volume]);

  const playNotificationSound = () => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(console.error);
    }
  };

  useEffect(() => {
    const initializeApp = async () => {
      try {
        const [currentResponse, limitsResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/current`),
          axios.get(`${API_BASE_URL}/api/limits`)
        ]);
        
        const newTicket = currentResponse.data.current_number || 0;
        setCurrentTicket(newTicket);
        previousTicketRef.current = newTicket;
        setTicketLimits(limitsResponse.data || { min: 0, max: 99 });
        setConnectionStatus('Conectado');
        setLoading(false);
      } catch (error) {
        console.error('Error obteniendo datos iniciales:', error);
        setConnectionStatus('Error de conexión');
        setLoading(false);
        setTicketLimits({ min: 0, max: 99 });
      }

      const newSocket = io(API_BASE_URL, socketConfig);
      
      newSocket.on('connect', () => {
        setConnectionStatus('Conectado');
      });

      newSocket.on('ticketUpdated', (ticketNumber) => {
        setPreviousTicket(currentTicket);
        setCurrentTicket(ticketNumber);
        
        if (ticketNumber !== previousTicketRef.current) {
          playNotificationSound();
          previousTicketRef.current = ticketNumber;
        }
      });

      newSocket.on('disconnect', () => {
        setConnectionStatus('Desconectado');
      });

      return () => newSocket.disconnect();
    };

    initializeApp();
  }, []);

  const toggleSound = () => setSoundEnabled(!soundEnabled);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p>Iniciando sistema...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <DisplayPanel 
        currentTicket={currentTicket}
        previousTicket={previousTicket}
        ticketLimits={ticketLimits}
        connectionStatus={connectionStatus}
        soundEnabled={soundEnabled}
        volume={volume}
        isMobile={isMobile}
        onToggleSound={toggleSound}
        onVolumeChange={handleVolumeChange}
      />
    </div>
  );
}

export default App;